import React, { useCallback, useRef, useState } from "react";
import { ReactNode, CSSProperties } from "react";
import { cn } from "../../lib/utils";

export const Table = ({
  children,
  wrapperClassName,
  layout = "auto",
  className,
}: {
  children: ReactNode;
  wrapperClassName?: string;
  layout?: "auto" | "fixed";
  /** Applied to the <table> element (e.g. for column borders) */
  className?: string;
}) => (
  <div className={cn("relative", wrapperClassName)}>
    <table
      className={cn(
        "w-full border border-gray-100 tracking-wide",
        layout === "fixed" ? "table-fixed" : "table-auto",
        className
      )}
    >
      {children}
    </table>
  </div>
);

export const TableBody = ({ children }: { children: ReactNode }) => (
  <tbody>{children}</tbody>
);

export const TableCell = ({
  children,
  style,
  className,
  colSpan,
}: {
  children?: ReactNode;
  style?: CSSProperties;
  className?: string;
  colSpan?: number;
}) => (
  <td
    style={style}
    className={`h-14 px-6 font-normal tracking-[0em] ${className}`}
    colSpan={colSpan}
  >
    {children}
  </td>
);

interface TableColumnHeaderProps {
  children: React.ReactNode;
  className?: string;
  isSticky?: boolean;
  isScrolling?: boolean;
  width?: string;
  isFirst?: boolean;
  isLast?: boolean;
  colSpan?: number;
  onClick?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

export const TableColumnHeader: React.FC<TableColumnHeaderProps> = ({
  children,
  className,
  isSticky,
  isScrolling,
  width,
  isFirst,
  isLast,
  colSpan,
  onClick,
  onMouseEnter,
  onMouseLeave,
}) => {
  return (
    <th
      className={cn(
        "h-14 px-6 text-left relative text-gray-700 text-[13px] font-medium leading-4 bg-gray-50",
        isSticky &&
        `sticky left-0 z-10 bg-gray-50 ${isScrolling
          ? "after:content-[''] after:absolute after:top-0 after:right-0 after:bottom-0 after:w-[1px] after:bg-gray-200"
          : ""
        }`,
        isFirst && "first:rounded-l-lg",
        isLast && "rounded-r-lg",
        className
      )}
      colSpan={colSpan}
      style={width ? { width, minWidth: width } : undefined}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="flex items-center h-full">{children}</div>
    </th>
  );
};

const MIN_COL_WIDTH = 48;
const RESIZE_HANDLE_WIDTH = 6;

interface ResizableTableColumnHeaderProps {
  children: React.ReactNode;
  columnId: string;
  width: number;
  minWidth?: number;
  className?: string;
  onResize: (columnId: string, newWidth: number) => void;
}

export const ResizableTableColumnHeader: React.FC<ResizableTableColumnHeaderProps> = ({
  children,
  columnId,
  width,
  minWidth = MIN_COL_WIDTH,
  className,
  onResize,
}) => {
  const [isResizing, setIsResizing] = useState(false);
  const startRef = useRef({ x: 0, width: 0 });

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      startRef.current = { x: e.clientX, width };
      setIsResizing(true);
    },
    [width]
  );

  React.useEffect(() => {
    if (!isResizing) return;
    const handleMouseMove = (e: MouseEvent) => {
      const delta = e.clientX - startRef.current.x;
      const newWidth = Math.max(minWidth, startRef.current.width + delta);
      onResize(columnId, newWidth);
    };
    const handleMouseUp = () => setIsResizing(false);
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isResizing, columnId, minWidth, onResize]);

  const pxWidth = `${width}px`;
  return (
    <th
      className={cn(
        "h-14 px-6 text-left relative text-gray-700 text-[13px] font-medium leading-4 bg-gray-50 group",
        className
      )}
      style={{ width: pxWidth, minWidth: pxWidth, maxWidth: pxWidth }}
    >
      <div className="flex items-center h-full truncate pr-1">{children}</div>
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize column"
        onMouseDown={handleMouseDown}
        className={cn(
          "absolute top-0 right-0 w-1.5 h-full cursor-col-resize touch-none",
          "hover:bg-primary-200 active:bg-primary-400",
          isResizing && "bg-primary-400"
        )}
        style={{ right: 0 }}
      />
    </th>
  );
};

interface TableHeaderRowProps {
  children: React.ReactNode;
  className?: string;
}

export const TableHeaderRow: React.FC<TableHeaderRowProps> = ({
  children,
  className,
}) => <tr className={cn("hover:bg-transparent", className)}>{children}</tr>;

interface TableHeaderProps {
  children: React.ReactNode;
  className?: string;
}

export const TableHeader: React.FC<TableHeaderProps> = ({
  children,
  className,
}) => <thead className={cn("bg-gray-50", className)}>{children}</thead>;

export const TableRow = ({
  children,
  className,
  onClick,
}: {
  children: ReactNode;
  className?: string;
  onClick?: () => void;
}) => (
  <tr className={className} onClick={onClick}>
    {children}
  </tr>
);
